import { useCallback, useState } from 'react';
import { useApiFetch } from '../api';
import type {
  DomainOrderCreateRequest,
  DomainOrderCreateResult,
  DomainTradeOrderCreateRequest,
  DomainTransferOrderCreateRequest,
} from '../types/models/DomainOrder';
import type { PaymentSource } from '../types/models/Payment';
import type { ApiResponseDomainOrderCreate } from '../types/api/ApiResponseDomainOrder';

/** Accepts a registration, transfer or trade create-order body (all hit `POST /domain-orders`). */
export type DomainOrderCreatePayload =
  | DomainOrderCreateRequest
  | DomainTransferOrderCreateRequest
  | DomainTradeOrderCreateRequest;

/**
 * Backend field errors keyed by the wizard's logical sections, so the caller can jump back to the
 * offending step. `contact` is itself a nested `{field: message}` map (or, rarely, a single string).
 * `items` is the transfer per-name error: a flat string **or** a `{name: code}` map (jump to step 1
 * and flag those rows). `domains` is a flat string on a registration, but on a trade it can also be
 * the per-name `{name: rejectionCode}` map (jump to step 1 and flag those rows).
 */
export interface DomainOrderFieldErrors {
  domains?: Record<string, string> | string;
  items?: Record<string, string> | string;
  contact?: Record<string, string> | string;
  nameservers?: string;
  dns_endpoint_id?: string;
  /** Transfer-only: `keep_contact` was rejected (non-boolean, or sent on a registration). */
  keep_contact?: string;
  agreements_accepted?: string;
  /** The `payment` object was rejected (bad source / unknown card / save_card type) — jump to the recap. */
  payment?: string;
}

export type DomainOrderCreateOutcome =
  | { status: 'success'; data: DomainOrderCreateResult }
  | { status: 'error'; message: string; fieldErrors: DomainOrderFieldErrors };

interface UseCreateDomainOrderReturn {
  /** Create the order (registration, transfer or trade). Resolves to a discriminated outcome (never throws). */
  create: (payload: DomainOrderCreatePayload) => Promise<DomainOrderCreateOutcome>;
  loading: boolean;
}

/**
 * Mutation hook for `POST /datacenters/domain-orders`. On success returns `{order_id, checkout_url,
 * payment_id, payment_waived, payment_source, paid}` — the caller redirects the browser to
 * `checkout_url` when there is one; for a `wallet`/`card` payment (no redirect) and for a waived
 * staff order, `checkout_url` is `null` and the caller lands on the order return page instead. On failure it returns a
 * structured outcome carrying the top-level `message` plus a per-section field-error map (so the
 * wizard can jump to the offending step) — JSend errors arrive at HTTP 200, so success is branched
 * on `body.status`.
 */
export const useCreateDomainOrder = (): UseCreateDomainOrderReturn => {
  const apiFetch = useApiFetch();
  const [loading, setLoading] = useState(false);

  const create = useCallback(
    async (payload: DomainOrderCreatePayload): Promise<DomainOrderCreateOutcome> => {
      setLoading(true);
      try {
        const response = await apiFetch('/datacenters/domain-orders', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        const body = (await response.json().catch(() => null)) as ApiResponseDomainOrderCreate | null;

        if (body?.status === 'success' && typeof body.data?.order_id === 'number') {
          const waived = body.data.payment_waived === true;
          const checkoutUrl =
            typeof body.data.checkout_url === 'string' && body.data.checkout_url !== '' ? body.data.checkout_url : null;
          const source = body.data.payment_source;
          const paymentSource: PaymentSource | null =
            source === 'checkout' || source === 'wallet' || source === 'card' ? source : null;
          // A success needs somewhere to go: a checkout to redirect to, a wallet/card payment that
          // settled (or is settling) server-side, or an explicit waiver (staff order). Anything else
          // is malformed — fall through to error.
          if (waived || checkoutUrl !== null || paymentSource === 'wallet' || paymentSource === 'card') {
            return {
              status: 'success',
              data: {
                order_id: body.data.order_id,
                checkout_url: checkoutUrl,
                payment_id: typeof body.data.payment_id === 'string' ? body.data.payment_id : null,
                payment_waived: waived,
                payment_source: paymentSource,
                paid: body.data.paid === true,
              },
            };
          }
        }

        const d = body?.data;
        const fieldErrors: DomainOrderFieldErrors = {
          domains: d?.domains,
          items: d?.items,
          contact: d?.contact,
          nameservers: typeof d?.nameservers === 'string' ? d.nameservers : undefined,
          dns_endpoint_id: typeof d?.dns_endpoint_id === 'string' ? d.dns_endpoint_id : undefined,
          keep_contact: typeof d?.keep_contact === 'string' ? d.keep_contact : undefined,
          agreements_accepted: typeof d?.agreements_accepted === 'string' ? d.agreements_accepted : undefined,
          payment:
            [d?.payment, d?.['payment.source'], d?.payment_method_id, d?.save_card].find(
              (v): v is string => typeof v === 'string',
            ) ?? undefined,
        };
        return {
          status: 'error',
          message: body?.message ?? 'Could not create your order. Please review your details and try again.',
          fieldErrors,
        };
      } catch (err) {
        return {
          status: 'error',
          message: err instanceof Error ? err.message : 'An unexpected error occurred while creating your order.',
          fieldErrors: {},
        };
      } finally {
        setLoading(false);
      }
    },
    [apiFetch],
  );

  return { create, loading };
};
